import AppConstants from '../Constants/AppConstants';
import networkService from '../Network/NetworkManager';
import {
  loadSubscriptionEntitlements,
  resetSubscriptionEntitlements,
} from './SubscriptionEntitlements';

const APPURL = require('../Constants/URLConstants');

const SUBSCRIPTIONS_PATH = 'skua/subscriptions';

// Names shown in the upgrade popup -> the active Skua plan unique_id (each has a Stripe price).
export const UPGRADE_PLAN_KEYS = {
  superstarter: '2025-superstarter',
  basic: '2025-superstarter',
  starter: '2025-starter',
  growth: '2025-growth',
  scale: '2025-scale-monthly',
  go: 'go-monthly-2026',
  genius: 'genius-monthly-2026',
};

// Pricing is grouped in the frontend: every plan is one row in Skua. Order = low to high.
export const PLAN_FAMILIES = {
  marketplace: ['basic', 'starter', 'growth', 'scale'],
  creator: ['go', 'genius'],
};

// Which family a subscribed plan unique_id belongs to (null = Community / none).
export const getPlanFamily = lookupKey => {
  const key = String(lookupKey || '').toLowerCase();
  if (/(^|[-_])(go|genius)([-_]|$)/.test(key)) return 'creator';
  if (/(starter|growth|scale)/.test(key)) return 'marketplace';
  return null;
};

// Position of the subscribed plan inside its family (-1 = not on a paid plan).
export const getPlanRank = lookupKey => {
  const family = getPlanFamily(lookupKey);
  if (!family) return -1;
  // Basic is sold as 2025-superstarter
  const key = String(lookupKey).toLowerCase().replace('superstarter', 'basic');
  return PLAN_FAMILIES[family].findIndex(name =>
    new RegExp(`(^|[-_])${name}([-_]|$)`).test(key),
  );
};

// Plans temporarily unavailable for new selection (matched against the plan unique_id).
// Remove an entry to bring the plan back. Tenants already on the plan keep it.
export const PAUSED_PLANS = ['community'];

export const isPlanPaused = name =>
  PAUSED_PLANS.some(paused => String(name || '').toLowerCase().includes(paused));

// Plans not offered for now (matched against the plan id/name). Tenants already on one still see it.
export const HIDDEN_PLANS = ['scale'];

export const isPlanHidden = name =>
  HIDDEN_PLANS.some(hidden => String(name || '').toLowerCase().includes(hidden));

// Role management is a Growth feature (Growth/Scale, or Genius on the creator ladder).
// Community, Starter and Go stay locked; same rule as Skua's rbac_plan middleware.
export const isRbacLocked = lookupKey => {
  // Internal (testing) workspaces are not limited by plans, same as Skua's rbac_plan
  if (AppConstants.isInternal === true || AppConstants.isInternal === 1 || AppConstants.isInternal === 'true') return false;
  const key = String(lookupKey || '').toLowerCase();
  return !key || /community|starter|(^|[-_])go([-_]|$)/.test(key);
};

export const resolvePlanKey = name =>
  UPGRADE_PLAN_KEYS[String(name || '').toLowerCase()] || name;

// Upgrade plan name -> pricing plan id, to find its yearly row (same matching as the workspace plans page)
const PRICING_PLAN_IDS = {
  superstarter: '2025-superstarter',
  basic: '2025-superstarter',
  starter: '2025-starter',
  growth: '2025-growth',
  scale: '2025-scale',
  go: 'go',
  genius: 'genius',
};
const matchesPricingId = (planName, id) =>
  id === 'go' ? /^go-/.test(planName || '') : String(planName || '').includes(id);
const intervalOf = plan =>
  plan.recurring_type || (/yearly/i.test(plan.name || '') ? 'yearly' : 'monthly');

// Price per upgrade plan name (lowercase) -> {amount, currency, key}, from Skua's plans.
// Yearly prices are keyed `${name}:yearly`; key is the Skua plan to select.
// source: the screen showing these plans ('upgrade_panel'), so Skua counts it as a pricing view
export const loadPlanPrices = async source => {
  const response = await networkService.networkCall(
    `${APPURL.URLPaths.subscriptionsPlans}${source ? `?source=${source}` : ''}`,
    'GET',
    '',
  );
  const plans = (response?.data ?? response)?.plans || [];
  const prices = {};
  Object.entries(UPGRADE_PLAN_KEYS).forEach(([name, key]) => {
    const plan = plans.find(item => item.name === key);
    if (plan && plan.amount != null) {
      prices[name] = {amount: plan.amount, currency: plan.currency || 'USD', key, limits: plan.limits};
    }
    const id = PRICING_PLAN_IDS[name];
    const yearly = id && plans.find(item => matchesPricingId(item.name, id) && intervalOf(item) === 'yearly');
    if (yearly && yearly.amount != null) {
      prices[`${name}:yearly`] = {amount: yearly.amount, currency: yearly.currency || 'USD', key: yearly.name, limits: yearly.limits};
    }
  });
  return prices;
};

const unwrap = response => response?.data ?? response;
const messageOf = response =>
  response?.error?.message ||
  response?.message ||
  'Could not change the plan. Please try again.';

// Same rules as the Tradly workspace plans page: a tenant on Community or on a trial
// goes through Stripe checkout; a tenant on a paid plan changes plan directly.
export const selectPlan = async ({planName, annual = false}) => {
  const planKey = resolvePlanKey(planName);
  const plansResponse = await networkService.networkCall(
    APPURL.URLPaths.subscriptionsPlans,
    'GET',
    '',
  );
  if (plansResponse?.status !== true) {
    return {ok: false, error: messageOf(plansResponse)};
  }

  const plans = unwrap(plansResponse)?.plans || [];
  if (isPlanPaused(planKey)) {
    return {ok: false, error: 'This plan is temporarily unavailable.'};
  }
  const target = plans.find(plan => plan.name === planKey);
  if (!target) {
    return {ok: false, error: 'This plan is not available right now.'};
  }

  const current =
    plans.find(plan => plan.subscribed && !plan.expired) ||
    plans.find(plan => plan.subscribed) ||
    null;
  if (current?.name === planKey && !current.trial) {
    return {ok: true, already: true};
  }

  // Each plan row carries its own interval (yearly plans have their own name).
  const recurring_type = target.recurring_type || (annual ? 'yearly' : 'monthly');
  const needsCheckout =
    !current || String(current.name).includes('community') || current.trial;

  if (needsCheckout) {
    const currency =
      target.currency || plans.find(plan => plan.currency)?.currency || 'USD';
    const response = await networkService.networkCall(
      `${SUBSCRIPTIONS_PATH}/stripe_session`,
      'POST',
      JSON.stringify({
        plan_name: planKey,
        currency,
        recurring_type,
        // Stripe returns here (Skua only accepts Tradly domains).
        // The plan travels with the return so a cancelled checkout can offer "Try again".
        return_url: `${window.location.origin}/workspace/plans?${new URLSearchParams({plan: planKey, interval: recurring_type}).toString()}`,
      }),
    );
    const sessionUrl = unwrap(response)?.session_url;
    if (response?.status === true && sessionUrl) {
      window.location.assign(sessionUrl);
      return {ok: true, redirected: true};
    }
    return {ok: false, error: messageOf(response)};
  }

  const response = await networkService.networkCall(
    SUBSCRIPTIONS_PATH,
    'PATCH',
    JSON.stringify({plan_name: planKey, recurring_type}),
  );
  if (response?.status === true) {
    resetSubscriptionEntitlements();
    await loadSubscriptionEntitlements();
    return {ok: true};
  }

  // Skua code 205: no card on file. Save one on Stripe's hosted page, then come back and
  // finish the change (the plans page reads these query params and retries).
  if (response?.error?.code === 205) {
    const query = new URLSearchParams({card_added: '1', plan: planKey, interval: recurring_type});
    const setup = await networkService.networkCall(
      `${SUBSCRIPTIONS_PATH}/stripe/setup_session`,
      'POST',
      JSON.stringify({return_url: `${window.location.origin}/workspace/plans?${query.toString()}`}),
    );
    const setupUrl = unwrap(setup)?.session_url;
    if (setup?.status === true && setupUrl) {
      window.location.assign(setupUrl);
      return {ok: true, redirected: true};
    }
    return {ok: false, error: messageOf(setup)};
  }
  return {ok: false, error: messageOf(response)};
};
